import React, { useEffect, useState } from 'react';

import { MapContainer, ImageOverlay, Marker, Popup } from 'react-leaflet';
import { Tag } from "antd";
import L from 'leaflet';

import 'leaflet-mouse-position/src/L.Control.MousePosition';

import './PropertiesPage.css'

import placeholderImage from '../../img/placeholder.svg'

type TabType = 'overview' | 'map' | 'gallery';

const API_URL = 'https://ss-rp.com/api/v1/property/list/';

const MAP_IMAGE_URL = `${process.env.PUBLIC_URL ?? ''}/samp/tiles/sat.jpg`;

const ICON_BASE_URL = `${process.env.PUBLIC_URL ?? ''}/samp/icons/`;

// SA-MP map configuration (from original code)
const MAP_SIDE_LENGTH = 6000.0;
const TOP_LEFT_X = -3000.0;
const TOP_LEFT_Y = 3000.0;

// Image dimensions
const IMAGE_WIDTH = 6000;
const IMAGE_HEIGHT = 6000;

interface Property {
  id: number;
  propX: number;
  propY: number;
  address: string;
  template_id: number;
  owner: number;
  type_owner: number;
  owned: boolean;
  price: number;
  prop_name: string;
  activated: boolean;
  world: number;
}

function numberWithCommas(x: number): string {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function removeHexCodes(str: string): string {
  return str.replace(/\{[0-9a-fA-F]{6}\}/g, '');
}

const icons: Record<string, string> = {
  'Icon_38.gif': `${ICON_BASE_URL}Icon_38.gif`,
  'Icon_6.gif': `${ICON_BASE_URL}Icon_6.gif`,
  'Icon_45.gif': `${ICON_BASE_URL}Icon_45.gif`,
  'Icon_63.gif': `${ICON_BASE_URL}Icon_63.gif`,
  'Icon_27.gif': `${ICON_BASE_URL}Icon_27.gif`,
  'Icon_21.gif': `${ICON_BASE_URL}Icon_21.gif`,
  'Icon_10.gif': `${ICON_BASE_URL}Icon_10.gif`,
  'Icon_14.gif': `${ICON_BASE_URL}Icon_14.gif`,
  'Icon_29.gif': `${ICON_BASE_URL}Icon_29.gif`,
  'Icon_49.gif': `${ICON_BASE_URL}Icon_49.gif`,
  'Icon_50.gif': `${ICON_BASE_URL}Icon_50.gif`,
  'Icon_30.gif': `${ICON_BASE_URL}Icon_30.gif`,
  'Icon_31.gif': `${ICON_BASE_URL}Icon_31.gif`,
  'Icon_32.gif': `${ICON_BASE_URL}Icon_32.gif`,
  'Icon_20.gif': `${ICON_BASE_URL}Icon_20.gif`,
  'Icon_22.gif': `${ICON_BASE_URL}Icon_22.gif`,
  'Icon_52.gif': `${ICON_BASE_URL}Icon_52.gif`
};

function GetIconFromTemplateID(templateID: number, ownerID: number, ownerType: number): string {
  switch(templateID)
  {
      case 1:
          return 'Icon_38.gif';
      case 3:
          return 'Icon_6.gif';
      case 2:
      case 4:
      case 5:
      case 6:
      case 7:
      case 8:
          return 'Icon_45.gif';
      case 9:
          return 'Icon_63.gif';
      case 10:
          return 'Icon_27.gif';
      case 11:
          return 'Icon_21.gif';
      case 12:
          return 'Icon_10.gif';
      case 13:
          return 'Icon_14.gif';
      case 14:
          return 'Icon_29.gif';
      case 15:
          return 'Icon_49.gif';
      case 16:
          return 'Icon_50.gif';
      default:
          {
              if(ownerType === 3 && (ownerID === 3 || ownerID === 7 || ownerID === 10))
                  return 'Icon_30.gif';
              else if(ownerType === 3 && ownerID === 8)
                  return 'Icon_22.gif';
              else if(ownerType === 3 && ownerID === 9)
                  return 'Icon_20.gif';
              else if(ownerType === 0 && (ownerID > 0))
                  return 'Icon_32.gif';
              else if(ownerType === 0 && (ownerID < 1))
                  return 'Icon_31.gif';
              else
                  return 'Icon_52.gif';
          }
  }
}

const PropertyMap: React.FC = () => {
  const [propertiesList, setPropertiesList] = React.useState<Property[] | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  useEffect(() => {
    fetch(API_URL, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then((json: Property[]) => {
      setPropertiesList(json);
      console.log(json.length);
    });
  }, []); // Added dependency array to prevent infinite loop

  const mapExtent = [1022.5, 1022.5, 1022.5, 1022.5];
  const mapMinZoom = 0;
  const mapMaxZoom = 3;
  const mapMaxResolution = 1;
  const mapMinResolution = Math.pow(2, mapMaxZoom) * mapMaxResolution;
  const tileExtent = [0, 0, 0, 0];

  // Use Leaflet's Simple CRS for image coordinates
  const bounds: L.LatLngBoundsExpression = [[0, 0], [IMAGE_HEIGHT, IMAGE_WIDTH]];

  // Convert SA-MP world coordinates to image pixel coordinates
  // SA-MP map ranges from -3000 to 3000 on both X and Y axes
  // Image is 6000x6000 pixels, (0,0) at top-left
  function sampToImageCoords(sampX: number, sampY: number): [number, number] {
    // Convert X: -3000 to 3000 → 0 to 6000
    const imageX = sampX + 3000;

    // Convert Y: 3000 to -3000 → 0 to 6000 (Y is inverted)
    const imageY = 3000 - sampY;

    console.log(`SAMP (${sampX}, ${sampY}) -> Image [${imageY}, ${imageX}]`);

    return [imageY, imageX]; // Leaflet uses [lat, lng] which maps to [y, x]
  }

  function addToMap(
    x: number,
    y: number,
    a: string,
    templateID: number,
    ownerID: number,
    ownerType: number,
    owned: boolean,
    price: number,
    propName: string,
    id: number
  ): JSX.Element {
    const [posY, posX] = sampToImageCoords(x, y);

    const customIcon = L.icon({
      iconUrl: icons[GetIconFromTemplateID(templateID, ownerID, ownerType)],
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16],
      className: 'property-marker'
    });

    return (
      <Marker position={[posY, posX]} icon={customIcon} key={id} zIndexOffset={1000}>
        <Popup>
          {ownerType === 0 && <>
            <img width='150px' height='100px' src={placeholderImage} alt="Property"></img>
            <hr></hr>
            {owned ? <Tag color="red">Owned</Tag> : <Tag color="green">For sale</Tag>}
            <br></br>
            <a href={("/property/" + id)}>{propName}</a>
            <br></br>
            <b>Address:</b> {a}
            <br></br>
            <b>Value:</b> ${numberWithCommas(price)}
        </>}

        {ownerType !== 0 && <>
          {owned ? <Tag color="red">Owned</Tag> : <Tag color="green">For sale</Tag>}
          <br></br>
          {propName}
          <br></br>
          <b>Address:</b> {a}
          <br></br>
          <b>Value:</b> ${numberWithCommas(price)}
        </>}
        </Popup>
      </Marker>
    );
  }

  return(
    <div className="property-page">
      <div className="property-card">
        <div className="property-card-body">
          <div>
            <h1 className="property-title">
              96 Ganton, Los Santos
              <span className="badge for-sale">For sale</span>
            </h1>
            <p className="property-location">
              <strong>Location:</strong> Los Santos
            </p>
          </div>
        </div>
      </div>

      <div className="property-content">
        <div className="tabs">
          <button
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={activeTab === 'map' ? 'active' : ''}
            onClick={() => setActiveTab('map')}
          >
            Map
          </button>
          <button
            className={activeTab === 'gallery' ? 'active' : ''}
            onClick={() => setActiveTab('gallery')}
          >
            Gallery
          </button>
        </div>

        <div style={{ marginTop: '16px' }}>
          {activeTab === 'overview' && (
            <div className="overview-panel">
              <p>Property overview content goes here.</p>
            </div>
          )}

          {activeTab === 'map' && (
            <div className="property-map-container">
              <MapContainer
                center={[3000, 3000]}
                zoom={1}
                scrollWheelZoom={true}
                maxZoom={mapMaxZoom}
                minZoom={mapMinZoom}
                maxBounds={bounds}
                maxBoundsViscosity={1.0}
                crs={L.CRS.Simple}
                style={{ background: '#1a1a1a' }}
              >
                <ImageOverlay
                  url={MAP_IMAGE_URL}
                  bounds={bounds}
                  opacity={1}
                  interactive={false}
                />
                {propertiesList && <>
                  {propertiesList.map((property) => {
                    if(property.activated && property.world === 0) {
                      return addToMap(
                        property.propX,
                        property.propY,
                        property.address,
                        property.template_id,
                        property.owner,
                        property.type_owner,
                        property.owned,
                        property.price,
                        removeHexCodes(property.prop_name),
                        property.id
                      );
                    }
                    return null;
                  })}
                </>}
              </MapContainer>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div className="gallery-panel">
              <div className="gallery-grid">
                <img src={placeholderImage} alt="Property" />
                <img src={placeholderImage} alt="Property" />
                <img src={placeholderImage} alt="Property" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PropertyMap;
